import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import Axios from "axios";
import Organization from "@/classes/organization";
import APIOrganization from "@/api/auth/organization";
import Station from "@/classes/station";
import Property from "@/classes/property";
import LZString from "lz-string";

let firstRefresh = true;

@Module({
  namespaced: true,
  name: "Global"
})
export default class Global extends VuexModule {
  theme = "light";
  appVersion = "";
  latestAppVersion = "";
  newVersionAvailable = false;
  hasTemporaryPassword = false;
  network = LZString.compress(JSON.stringify([]));

  @Mutation
  setTheme(val: string) {
    this.theme = val;
  }

  @Mutation
  updateHasTemporaryPassword(val: boolean) {
    this.hasTemporaryPassword = val;
  }

  @Mutation
  updateAppVersion(version: string) {
    this.appVersion = version;
  }

  @Mutation
  updateLatestAppVersion({ version, compareVersions = true }) {
    if (this.latestAppVersion === "") {
      this.latestAppVersion = version;
      return;
    }
    if (compareVersions && this.latestAppVersion !== this.appVersion) {
      this.newVersionAvailable = true;
    }
    this.latestAppVersion = version;
  }

  @Mutation
  acknowledgeAppVersionChanged() {
    this.newVersionAvailable = false;
    this.appVersion = this.latestAppVersion;
  }

  @Action
  async refreshAppVersion() {
    const version = (await Axios.get<{ version: string }>("/version.json")).data.version;
    const formattedVersion = `V${version}`;
    if (firstRefresh) {
      this.updateAppVersion(formattedVersion);
      this.updateLatestAppVersion({ version: formattedVersion, compareVersions: false });
      firstRefresh = false;
    } else {
      if (!this.appVersion) {
        this.updateAppVersion(formattedVersion);
      }
      this.updateLatestAppVersion({ version: formattedVersion });
    }
    setTimeout(() => this.context.dispatch("refreshAppVersion"), 30 * 1000);
  }

  @Mutation
  setNetwork(network: Organization[]) {
    this.network = LZString.compress(JSON.stringify(network));
  }

  @Action
  async fetchNetwork(): Promise<Organization[]> {
    // We'll return a promise if there is no value in the store
    // This is so that we can keep a loading spinner on the first load
    return new Promise((resolve, reject) => {
      let resolved = false;
      if (this.network.length) {
        resolved = true;
        if (typeof this.network === "object") {
          resolve(this.network as Organization[]);
        } else {
          resolve(JSON.parse(LZString.decompress(this.network) || "[]") as Organization[]);
        }
      }

      // The below will run regardless of the above if condition
      APIOrganization.findAllWithFullTree()
        .then(organizations => {
          this.setNetwork(
            organizations.map(
              o =>
                new Organization({
                  ...o,
                  properties: o.properties.map(
                    p =>
                      new Property({
                        ...p,
                        stations: p.stations.map(
                          s =>
                            new Station({
                              id: s.id,
                              qrCode: s.qrCode,
                              serialNo: s.serialNo,
                              status: s.status,
                              // Heading
                              // clusterId: s.clusterId,
                              // Admin controls
                              connector: s.connector,
                              // Uptime report
                              registeredTime: s.registeredTime,
                              // Advanced search
                              _isUnhealthy: s._isUnhealthy,
                              _isDisconnected: s._isDisconnected,
                              isGateway: s.isGateway,
                              isSubscription: s.isSubscription,
                              itemStatus: s.itemStatus,
                              stationLevel: s.stationLevel,
                              fundingType: s.fundingType,
                              stationType: s.stationType,
                              latitude: s.latitude,
                              longitude: s.longitude
                            })
                        )
                      })
                  )
                })
            )
          );

          if (!resolved) {
            resolve(organizations);
          }
        })
        .catch(e => reject(e));
    });
  }
}
